<script setup lang="ts">
    import { CellContentAlignment, CellContentJustification } from "@/components/KTable/helpers";
    import DOMPurify from "dompurify";
    import moment from "moment/moment";
    import { computed, toRefs, useSlots } from "vue";

    const props = withDefaults(
        defineProps<{
            data?:
                | {
                      [key: string]: unknown;
                  }
                | { storePath: string; id: string }
                | string
                | number
                | Date
                | boolean
                | unknown[];
            /**
             * Om content ska placeras top/center/bottom
             */
            alignContent?: CellContentAlignment;
            /**
             * Om content ska placeras left/center/right
             */
            justifyContent?: CellContentJustification;
            /**
             * Om content ska flyta ut vertikalt eller horisontellt
             */
            contentDirection?: "horizontal" | "vertical";
            /**
             * Ska innehållet kapas och brytas med ellipsis?
             */
            truncate?: boolean;
            /**
             * Innehållet kapas och brytas med ellipsis efter satt mängd rader istället för 1.
             * Används i kombination med truncate: true
             */
            lines?: number;
            /**
             * Typ av content som ska renderas
             */
            contentType?: "date" | "time" | "datetime" | "boolean" | "array";
            /**
             * Används för fullcalendar. Data som läses av när event släpps över kalender
             */
            dataEvent?: unknown;
            /**
             * Content som ska renderas i tooltip. Stöd för html-taggar br/span/p
             */
            tooltipContent?: string | ((props: { data: unknown }) => string);
            /**
             * Ska boolean värden visa Nej vid false
             */
            booleanShowNo?: boolean;
        }>(),
        {
            alignContent: CellContentAlignment.Top,
            justifyContent: CellContentJustification.Left,
            contentDirection: "horizontal",
            truncate: false,
            lines: 0,
            booleanShowNo: false,
        }
    );

    const { lines, contentType, data, tooltipContent, booleanShowNo } = toRefs(props);

    const slots = useSlots();

    const convertedData = computed(() => {
        if (contentType?.value) {
            if (["date", "time", "datetime"].includes(contentType.value) && data?.value) {
                return formatDateTime({
                    value: data.value as string | Date,
                    type: contentType.value as "date" | "datetime" | "time",
                });
            } else if (contentType.value === "boolean") {
                return data?.value ? "Ja" : booleanShowNo.value ? "Nej" : "";
            }
        }

        return data?.value;
    });

    const convertedTooltipContent = computed(() => {
        if (!tooltipContent?.value && slots.content) {
            return undefined;
        }

        if (tooltipContent?.value) {
            return DOMPurify.sanitize(
                typeof tooltipContent.value === "function"
                    ? tooltipContent.value({ data: data?.value })
                    : tooltipContent.value,
                {
                    ALLOWED_TAGS: ["br", "span", "p"],
                }
            );
        } else {
            return convertedData.value;
        }
    });

    const dateFormat = "yyyy-MM-DD";
    const timeFormat = "HH:mm";
    const datetimeFormat = `${dateFormat} ${timeFormat}`;

    const formatDateTime = ({ value, type }: { value: string | Date; type: "date" | "time" | "datetime" }) => {
        const momentDate = moment(value);

        if (type === "date") {
            return momentDate.format(dateFormat);
        } else if (type === "time") {
            return momentDate.format(timeFormat);
        } else {
            return momentDate.format(datetimeFormat);
        }
    };
</script>

<template>
    <div
        :class="[
            'k-cell',
            `align-${alignContent}`,
            contentDirection,
            { truncate, multiline: !!lines },
            `justify-${justifyContent}`,
        ]"
        :data-event="dataEvent"
        v-tooltip="
            truncate
                ? {
                      placement: 'top-start',
                      content: convertedTooltipContent,
                      theme: 'kliniken-tooltip',
                      skidding: 0,
                      arrowPadding: 0,
                      class: 'k-cell-tooltip',
                      html: true,
                  }
                : undefined
        "
    >
        <div class="content">
            <template v-if="!$slots.content && contentType && contentType === 'array'">
                <span v-for="(content, index) in convertedData" :key="index">
                    {{ content }}
                </span>
            </template>
            <template v-else-if="!$slots.content && (contentType || ['string', 'number'].includes(typeof data))">
                <span>{{ convertedData }}</span>
            </template>
            <template v-else>
                <slot name="content" :data="data">
                    <span></span>
                </slot>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
    .k-cell-tooltip {
        max-width: 40rem;

        .v-popper__arrow-container {
            left: 10px !important;
        }
    }
</style>

<style scoped lang="scss">
    .k-cell {
        height: 100%;
        width: 100%;
        font-size: 0.9rem;
        word-break: break-word;

        .content {
            display: grid;
            width: 100%;
            height: 100%;
            grid-auto-rows: max-content;
            grid-auto-columns: minmax(auto, max-content);
        }

        &.truncate {
            .content {
                span,
                p {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
            }

            &.multiline {
                .content {
                    span,
                    p {
                        display: -webkit-box;
                        -webkit-box-orient: vertical;
                        -webkit-line-clamp: v-bind(lines);
                        white-space: unset;
                    }
                }
            }
        }

        &.align-top {
            .content {
                align-items: flex-start;
            }
        }

        &.align-center {
            .content {
                align-items: center;
                grid-auto-rows: unset;
            }
        }

        &.align-bottom {
            .content {
                align-items: flex-end;
            }
        }

        &.horizontal {
            .content {
                grid-auto-flow: column;
            }
        }

        &.vertical {
            .content {
                grid-auto-flow: row;
            }
        }

        &.justify-center {
            .content {
                justify-items: center;
                justify-content: center;
            }
        }
    }
</style>
